// usePushPermission — wraps Notification.permission with a 4th state
// 'unsupported' for browsers that can't receive web push at all (iOS Safari
// tab, in-app webviews, very old browsers). See Eng-D5.

import { isSupported } from "firebase/messaging";
import { useEffect, useState } from "react";

export type PushPermission =
  | "default"
  | "granted"
  | "denied"
  | "unsupported";

function readPermission(): PushPermission {
  if (typeof window === "undefined") return "unsupported";
  if (typeof Notification === "undefined") return "unsupported";
  // iOS Safari tab (not added to home screen) reports permission='denied'
  // even before asking. We detect "is iOS && not standalone" elsewhere via
  // isSupported() — FCM's check already covers most of this.
  return Notification.permission as PushPermission;
}

export function usePushPermission(): {
  permission: PushPermission;
  request: () => Promise<PushPermission>;
} {
  const [permission, setPermission] = useState<PushPermission>(() =>
    readPermission(),
  );

  useEffect(() => {
    let cancelled = false;
    isSupported()
      .then((ok) => {
        if (cancelled) return;
        if (!ok) {
          setPermission("unsupported");
        } else {
          setPermission(readPermission());
        }
      })
      .catch(() => {
        if (!cancelled) setPermission("unsupported");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function request(): Promise<PushPermission> {
    if (typeof Notification === "undefined") return "unsupported";
    const result = await Notification.requestPermission();
    setPermission(result as PushPermission);
    return result as PushPermission;
  }

  return { permission, request };
}
